import React, {
    useState, useEffect, useCallback, useRef,
} from 'react';
import withStyles from 'react-jss';
import { CloseOutlined, CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons';
import {
    Modal, Input, Checkbox, Tabs,
} from 'antd';
import {
    SortableContainer,
    SortableElement,
    arrayMove,
} from 'react-sortable-hoc';
import { withTranslation, WithTranslation } from 'react-i18next';
import { CssType, ThemeType } from 'theme/jss-types';
import { DragIcon } from 'assets/DragIcon';
import {
    ColumnInfo,
    GroupedColumnDetail,
    GroupedColumnData,
} from 'components/consignments/consignment.types';

const { Search } = Input;
const { TabPane } = Tabs;

interface ColumnMetadata {
    tab_header: string;
    details: Array<{
        header: string;
        columns: ColumnInfo[];
    }>;
}

interface ExpandedHeaders {
    [key: string]: boolean;
}

interface CustomColumnsModalProps extends WithTranslation {
    classes: any;
    isVisible: boolean;
    onModalClose: () => void;
    onSubmit: (data: GroupedColumnData[]) => Promise<{ isSuccess: boolean }>;
    handleRefresh?: () => Promise<void>;
    detailsColumnsMetadata: ColumnMetadata[];
    detailsColumnsSelected: GroupedColumnData[];
    tabHeaderDisplayMap?: Record<string, string>;
}

const styles = (theme: ThemeType): CssType => ({
    listItemStyle: {
        marginTop: '9px',
        backgroundColor: '#D5E9FF',
        borderRadius: '4px',
        padding: '5px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        userSelect: 'none',
        width: '100%',
    },
    mainDiv: {
        height: '350px',
        display: 'flex',
        overflow: 'hidden',
        justifyContent: 'space-between',
    },
    leftDiv: {
        display: 'flex',
        flexDirection: 'column',
        width: '50%',
    },
    checkboxDiv: {
        overflow: 'auto',
        border: '1px solid #E8E8E8',
        height: '100%',
        padding: '16px',
    },
    customHeader: {
        fontWeight: 700,
        fontSize: '14px',
        color: '#000000',
        padding: '6px',
        borderBottom: '1px solid #E8E8E8',
        marginBottom: '12px',
    },
    listStyle: {
        height: '90%',
        overflow: 'auto',
    },
    customRightDiv: {
        width: '48%',
        overflow: 'hidden',
        '& ul': {
            padding: 0,
            'list-style-type': 'none',
        },
    },
    customPrimaryButton: {
        backgroundColor: theme.primaryColor,
        color: theme.primaryColorContrastText,
    },
    headerContainer: {
        marginBottom: '16px',
        backgroundColor: '#F8F8F8',
        borderRadius: '4px',
        padding: '12px 12px 0 12px',
        border: '1px solid #E8E8E8',
    },
    headerTitle: {
        fontWeight: 'bold',
        display: 'block',
        padding: '0 8px',
        borderRadius: '4px 4px 0 0',
    },
});

const getColumnId = (column: any): string => {
    return column.column_id || column.id || column.key || '';
};

const isGroupedFormat = (details: GroupedColumnDetail[]): boolean => {
    return details.some((d) => d.header !== undefined && d.columns);
};

const findColumnInMetadata = (
    columnId: string,
    metadata: ColumnMetadata,
): { header: string; column: ColumnInfo } | null => {
    const sectionWithColumn = metadata.details.find((section) => {
        return section.columns.some((column) => getColumnId(column) === columnId);
    });

    if (!sectionWithColumn) {
        return null;
    }

    const column = sectionWithColumn.columns.find((col) => getColumnId(col) === columnId);

    if (!column) {
        return null;
    }

    return {
        header: sectionWithColumn.header,
        column,
    };
};

/**
 * Initialize grouped data from selected data and metadata
 * - If selectedData is empty, auto-select all columns from metadata
 * - If a tab has empty details, auto-select all columns for that tab
 * - Otherwise, preserve existing selections
 */
const initializeGroupedData = (
    selectedData: GroupedColumnData[],
    metadata: ColumnMetadata[],
): GroupedColumnData[] => {
    if (!metadata?.length) return selectedData || [];
    if (!selectedData?.length) {
        // Auto-select all columns when selectedData is empty
        return metadata.map((tab) => ({
            tab_header: tab.tab_header,
            details: tab.details.map((section) => ({
                header: section.header,
                columns: [...section.columns],
            })),
        }));
    }

    // Build a map of tabs from selectedData
    const selectedTabMap = new Map(selectedData.map((g) => [g.tab_header, g]));

    // Process all tabs from metadata
    return metadata.map((tabMeta) => {
        const selectedTab = selectedTabMap.get(tabMeta.tab_header);

        // If tab not in selectedData or has empty details, auto-select all
        if (!selectedTab || !selectedTab.details?.length) {
            return {
                tab_header: tabMeta.tab_header,
                details: tabMeta.details.map((section) => ({
                    header: section.header,
                    columns: [...section.columns],
                })),
            };
        }

        // If already in grouped format, return as-is
        if (isGroupedFormat(selectedTab.details)) {
            return { ...selectedTab };
        }

        // Convert simple format to grouped format
        const groupedByHeader: { [header: string]: ColumnInfo[] } = {};
        selectedTab.details.forEach((detail) => {
            const columnId = getColumnId(detail);
            if (columnId) {
                const columnInfo = findColumnInMetadata(columnId, tabMeta);
                if (columnInfo) {
                    if (!groupedByHeader[columnInfo.header]) {
                        groupedByHeader[columnInfo.header] = [];
                    }
                    groupedByHeader[columnInfo.header].push(columnInfo.column);
                }
            }
        });

        return {
            tab_header: tabMeta.tab_header,
            details: Object.entries(groupedByHeader).map(([header, columns]) => ({
                header,
                columns,
            })),
        };
    });
};

const convertToSimpleFormat = (groupedData: GroupedColumnData[]): GroupedColumnData[] => {
    return groupedData.map((group) => {
        const simpleDetails: GroupedColumnDetail[] = [];

        group.details.forEach((detail) => {
            if (detail.header !== undefined && detail.columns) {
                detail.columns.forEach((col) => {
                    simpleDetails.push({ column_id: getColumnId(col) });
                });
            } else {
                const columnId = getColumnId(detail);
                if (columnId) {
                    simpleDetails.push({ column_id: columnId });
                }
            }
        });

        return { tab_header: group.tab_header, details: simpleDetails };
    });
};

const addColumnToGroup = (
    group: GroupedColumnData,
    columnHeader: string,
    columnInfo: ColumnInfo,
): GroupedColumnData => {
    const updatedDetails = [...group.details];
    const headerDetailIndex = updatedDetails.findIndex((d) => d.header === columnHeader);

    if (headerDetailIndex !== -1) {
        const headerDetail = updatedDetails[headerDetailIndex];
        if (headerDetail.columns) {
            const colExists = headerDetail.columns.some(
                (c) => getColumnId(c) === getColumnId(columnInfo),
            );
            if (!colExists) {
                updatedDetails[headerDetailIndex] = {
                    ...headerDetail,
                    columns: [...headerDetail.columns, columnInfo],
                };
            }
        } else {
            updatedDetails[headerDetailIndex] = {
                ...headerDetail,
                columns: [columnInfo],
            };
        }
    } else {
        updatedDetails.push({
            header: columnHeader,
            columns: [columnInfo],
        });
    }

    return { ...group, details: updatedDetails };
};

const removeColumnFromGroup = (
    group: GroupedColumnData,
    columnHeader: string,
    columnId: string,
): GroupedColumnData => {
    const updatedDetails = group.details
        .map((detail) => {
            if (detail.header === columnHeader && detail.columns) {
                const filteredColumns = detail.columns.filter((c) => getColumnId(c) !== columnId);
                if (filteredColumns.length === 0) return null;
                return { ...detail, columns: filteredColumns };
            }
            return detail;
        })
        .filter((d): d is GroupedColumnDetail => d !== null);

    return { ...group, details: updatedDetails };
};

const CustomColumnsModal: React.FC<CustomColumnsModalProps> = (props) => {
    const {
        classes,
        t,
        isVisible,
        onModalClose,
        onSubmit,
        handleRefresh,
        detailsColumnsMetadata,
        detailsColumnsSelected,
        tabHeaderDisplayMap,
    } = props;

    const [isUploading, setIsUploading] = useState(false);
    const [groupedData, setGroupedData] = useState<GroupedColumnData[]>([]);
    const [activeGroupedTab, setActiveGroupedTab] = useState<string>('');
    const [groupedSearchText, setGroupedSearchText] = useState<string>('');
    const [expandedHeaders, setExpandedHeaders] = useState<ExpandedHeaders>({});

    const initializedRef = useRef(false);

    useEffect(() => {
        const initialGroupedData = initializeGroupedData(
            detailsColumnsSelected,
            detailsColumnsMetadata,
        );

        setGroupedData(initialGroupedData);

        if (initialGroupedData.length > 0) {
            if (!activeGroupedTab || !initialGroupedData.find((g) => g.tab_header === activeGroupedTab)) {
                setActiveGroupedTab(initialGroupedData[0].tab_header);
            }
        }

        initializedRef.current = true;
    }, [detailsColumnsSelected, detailsColumnsMetadata]);

    const handleGroupedCheckboxChange = useCallback(
        (tabHeader: string, columnId: string, checked: boolean) => {
            const currentTabMetadata = detailsColumnsMetadata.find(
                (meta) => meta.tab_header === tabHeader,
            );

            if (!currentTabMetadata?.details) return;

            setGroupedData((prevData) => prevData.map((group) => {
                if (group.tab_header !== tabHeader) return group;

                const result = findColumnInMetadata(columnId, currentTabMetadata);
                if (!result) return group;

                const { header: columnHeader, column: columnInfo } = result;

                if (checked) {
                    return addColumnToGroup(group, columnHeader, columnInfo);
                }
                return removeColumnFromGroup(group, columnHeader, columnId);
            }));
        },
        [detailsColumnsMetadata],
    );

    const handleGroupedCrossClick = useCallback(
        (tabHeader: string, columnId: string) => {
            const currentTabMetadata = detailsColumnsMetadata.find(
                (meta) => meta.tab_header === tabHeader,
            );

            if (!currentTabMetadata?.details) return;

            setGroupedData((prevData) => prevData.map((group) => {
                if (group.tab_header !== tabHeader) return group;

                const result = findColumnInMetadata(columnId, currentTabMetadata);
                if (!result) return group;

                return removeColumnFromGroup(group, result.header, columnId);
            }));
        },
        [detailsColumnsMetadata],
    );

    const handleModalOk = useCallback(async () => {
        setIsUploading(true);

        const simpleFormatData = convertToSimpleFormat(groupedData);
        const result = await onSubmit(simpleFormatData);

        if (result?.isSuccess) {
            onModalClose();
            if (handleRefresh) {
                await handleRefresh();
            }
        } else {
            setIsUploading(false);
        }
    }, [groupedData]);

    const handleSortEnd = useCallback(
        (headerName: string, params: { oldIndex: number; newIndex: number }, items: any[]) => {
            const newItems = arrayMove([...items], params.oldIndex, params.newIndex);

            setGroupedData((prevData) => prevData.map((group) => {
                if (group.tab_header !== activeGroupedTab) return group;

                const updatedDetails = [...group.details];
                const detailIndex = updatedDetails.findIndex((d) => d.header === headerName);

                if (detailIndex !== -1 && updatedDetails[detailIndex].columns) {
                    updatedDetails[detailIndex] = {
                        ...updatedDetails[detailIndex],
                        columns: newItems.map((item) => {
                            const originalCol = updatedDetails[detailIndex].columns?.find(
                                (c) => getColumnId(c) === item.id,
                            );
                            return (
                                originalCol || {
                                    id: item.id,
                                    key: item.id,
                                    pretty_name: item.pretty_name,
                                    type: 'string',
                                }
                            );
                        }),
                    };
                }

                return { ...group, details: updatedDetails };
            }));
        },
        [activeGroupedTab],
    );

    const getSelectedColumnIds = (currentGroup: GroupedColumnData): string[] => {
        const columnIds: string[] = [];

        currentGroup.details.forEach((detail) => {
            if (detail.column_id) {
                columnIds.push(detail.column_id);
            } else if (detail.header !== undefined && detail.columns) {
                detail.columns.forEach((col) => {
                    const colId = getColumnId(col);
                    if (colId) columnIds.push(colId);
                });
            } else {
                const id = getColumnId(detail);
                if (id) columnIds.push(id);
            }
        });

        return columnIds.filter(Boolean);
    };

    const buildSelectedHeaderMap = (
        currentGroup: GroupedColumnData,
        currentTabMetadata: ColumnMetadata,
    ): { [header: string]: Array<{ id: string; pretty_name: string }> } => {
        const selectedHeaderMap: { [header: string]: Array<{ id: string; pretty_name: string }> } = {};

        currentGroup.details.forEach((detail) => {
            if (detail.header !== undefined && detail.columns) {
                const headerName = detail.header;
                if (!selectedHeaderMap[headerName]) {
                    selectedHeaderMap[headerName] = [];
                }
                detail.columns.forEach((col) => {
                    const colId = getColumnId(col);
                    if (colId) {
                        selectedHeaderMap[headerName].push({
                            id: colId,
                            pretty_name: col.pretty_name,
                        });
                    }
                });
            } else {
                const columnId = getColumnId(detail);
                if (columnId) {
                    const metaSectionWithColumn = currentTabMetadata.details.find((metaSection) => {
                        return metaSection.columns?.some((c) => getColumnId(c) === columnId);
                    });
                    const foundHeader = metaSectionWithColumn?.header || 'Other';
                    if (!selectedHeaderMap[foundHeader]) {
                        selectedHeaderMap[foundHeader] = [];
                    }

                    const metaCol = metaSectionWithColumn?.columns?.find(
                        (c) => getColumnId(c) === columnId,
                    )
                        || currentTabMetadata.details
                            .flatMap((s) => s.columns)
                            .find((c) => getColumnId(c) === columnId);

                    selectedHeaderMap[foundHeader].push({
                        id: columnId,
                        pretty_name: metaCol?.pretty_name || columnId,
                    });
                }
            }
        });

        return selectedHeaderMap;
    };

    const filterMetadata = (metadata: ColumnMetadata, searchText: string) => {
        if (!searchText) return metadata.details;

        return metadata.details
            .map((section) => ({
                ...section,
                columns: section.columns.filter((col) => col.pretty_name.toLowerCase().includes(searchText.toLowerCase())),
            }))
            .filter((section) => section.columns.length > 0);
    };

    const GroupedListItem: React.FC<{ columnId: string; prettyName: string }> = ({
        columnId,
        prettyName,
    }) => {
        const isDisabled = columnId === 'reference_number';

        return (
            <div className={classes.listItemStyle}>
                <div>
                    {!isDisabled && <DragIcon />}
                    <span style={{ marginLeft: '8px' }}>{t(prettyName)}</span>
                </div>
                {!isDisabled && (
                    <div>
                        <CloseOutlined
                            onClick={() => handleGroupedCrossClick(activeGroupedTab, columnId)}
                        />
                    </div>
                )}
            </div>
        );
    };

    interface SortableGroupedItemProps {
        columnId: string;
        prettyName: string;
    }

    const SortableGroupedItemComponent: React.FC<SortableGroupedItemProps> = ({
        columnId,
        prettyName,
    }) => {
        return <GroupedListItem columnId={columnId} prettyName={prettyName} />;
    };

    const SortableGroupedItem = SortableElement<SortableGroupedItemProps>(
        SortableGroupedItemComponent,
    );

    interface SortableGroupedListProps {
        items: Array<{ id: string; pretty_name: string }>;
    }

    const SortableGroupedListComponent: React.FC<SortableGroupedListProps> = ({ items }) => (
        <ul>
            {items.map((item, index) => {
                const isDisabled = item.id === 'reference_number';
                return (
                    <SortableGroupedItem
                        key={item.id}
                        index={index}
                        columnId={item.id}
                        prettyName={item.pretty_name}
                        disabled={isDisabled}
                    />
                );
            })}
        </ul>
    );

    const SortableGroupedList = SortableContainer<SortableGroupedListProps>(
        SortableGroupedListComponent,
    );

    const renderGroupedTabs = () => {
        if (!detailsColumnsMetadata?.length) {
            return <div style={{ padding: 20, textAlign: 'center' }}>No data available</div>;
        }

        const currentGroup = groupedData.find((g) => g.tab_header === activeGroupedTab);
        if (!currentGroup) {
            return <div style={{ padding: 20, textAlign: 'center' }}>No data available</div>;
        }

        const currentTabMetadata = detailsColumnsMetadata.find(
            (meta) => meta.tab_header === activeGroupedTab,
        );
        if (!currentTabMetadata?.details) {
            return (
                <div style={{ padding: 20, textAlign: 'center' }}>
                    No metadata available for this tab
                </div>
            );
        }

        const selectedColumnIds = getSelectedColumnIds(currentGroup);
        const selectedHeaderMap = buildSelectedHeaderMap(currentGroup, currentTabMetadata);
        const filteredMetadata = filterMetadata(currentTabMetadata, groupedSearchText);

        const isColumnSelected = (columnId: string): boolean => selectedColumnIds.includes(columnId);

        const getSectionColumnIds = (section: { columns: any[] }): string[] => {
            return section.columns.map((col) => getColumnId(col)).filter(Boolean);
        };

        return (
            <div className={classes.mainDiv}>
                {/* Left: Checkboxes with nested headers */}
                <div className={classes.leftDiv}>
                    <div style={{ padding: '12px', border: '1px solid #E8E8E8' }}>
                        <Search
                            onChange={(e) => setGroupedSearchText(e.target.value)}
                            placeholder={t('Search')}
                            value={groupedSearchText}
                        />
                    </div>
                    <div className={classes.checkboxDiv}>
                        {filteredMetadata.map((section) => {
                            const sectionColumnIds = getSectionColumnIds(section);
                            const selectedInSection = sectionColumnIds.filter((id) => isColumnSelected(id));
                            const allSelected = selectedInSection.length === sectionColumnIds.length
                                && sectionColumnIds.length > 0;
                            const someSelected = selectedInSection.length > 0
                                && selectedInSection.length < sectionColumnIds.length;
                            const isExpanded = expandedHeaders[section.header] !== false;

                            return (
                                <div key={section.header}>
                                    <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
                                        <Checkbox
                                            checked={allSelected}
                                            indeterminate={someSelected}
                                            value={section.header}
                                            onChange={(e) => {
                                                const { checked } = e.target;
                                                sectionColumnIds.forEach((colId) => {
                                                    handleGroupedCheckboxChange(activeGroupedTab, colId, checked);
                                                });
                                            }}
                                        >
                                            <b>{tabHeaderDisplayMap?.[section.header] || t(section.header)}</b>
                                        </Checkbox>
                                        <span style={{ marginLeft: '8px', cursor: 'pointer' }}>
                                            {isExpanded ? (
                                                <CaretDownOutlined
                                                    onClick={() => setExpandedHeaders((prev) => ({
                                                        ...prev,
                                                        [section.header]: false,
                                                    }))}
                                                />
                                            ) : (
                                                <CaretRightOutlined
                                                    onClick={() => setExpandedHeaders((prev) => ({
                                                        ...prev,
                                                        [section.header]: true,
                                                    }))}
                                                />
                                            )}
                                        </span>
                                    </div>

                                    {isExpanded
                                        && section.columns.map((column) => {
                                            const columnId = getColumnId(column);
                                            const isChecked = isColumnSelected(columnId);
                                            const isDisabled = columnId === 'reference_number';

                                            return (
                                                <div style={{ marginBottom: '12px', paddingLeft: '24px' }} key={columnId}>
                                                    <Checkbox
                                                        checked={isChecked}
                                                        value={columnId}
                                                        disabled={isDisabled}
                                                        onChange={(e) => handleGroupedCheckboxChange(
                                                            activeGroupedTab,
                                                            columnId,
                                                            e.target.checked,
                                                        )}
                                                    >
                                                        {t(column.pretty_name)}
                                                    </Checkbox>
                                                </div>
                                            );
                                        })}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Right: Selected columns grouped by header */}
                <div className={classes.customRightDiv}>
                    <div className={classes.customHeader}>{t('Details To Show')}</div>
                    <div className={classes.listStyle}>
                        {Object.entries(selectedHeaderMap).map(([headerName, itemsList]) => (
                            <div key={headerName} className={classes.headerContainer}>
                                <span className={classes.headerTitle}>
                                    {tabHeaderDisplayMap?.[headerName] || t(headerName)}
                                </span>
                                <SortableGroupedList
                                    distance={1}
                                    items={itemsList}
                                    onSortEnd={(params) => handleSortEnd(headerName, params, itemsList)}
                                    helperClass="sortableHelper"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Modal
            title={t('Customise')}
            open={isVisible}
            okText={t('Update')}
            cancelText={t('Cancel')}
            destroyOnClose
            maskClosable={false}
            width={800}
            confirmLoading={isUploading}
            onOk={handleModalOk}
            onCancel={onModalClose}
            okButtonProps={{ className: classes.customPrimaryButton, disabled: isUploading }}
        >
            <Tabs activeKey={activeGroupedTab} onChange={setActiveGroupedTab}>
                {groupedData.map((group) => {
                    const displayHeader = tabHeaderDisplayMap?.[group.tab_header] || group.tab_header;
                    return (
                        <TabPane tab={t(displayHeader)} key={group.tab_header}>
                            {renderGroupedTabs()}
                        </TabPane>
                    );
                })}
            </Tabs>
        </Modal>
    );
};

const StyledCustomModal = withStyles(styles, { injectTheme: true })(CustomColumnsModal);
export default withTranslation('translation')(StyledCustomModal);
